import { z } from 'zod';
import type { Via } from '../enums';
import { idSchema, localDateSchema, versionSchema } from './common';
import { taskTitleSchema } from './work';

/**
 * Szybkie notatki na „Dziś” — karteczki (DECISIONS 2026-10-17 [S]/[P] „Szybkie notatki na »Dziś« (karteczki)”):
 * „wrzutki” osoby, które nie są zadaniami („pojechać na pocztę”). Rekord `quick_notes` (workspace + autor), PRYWATNY
 * dla autora: notatki widzi i zmienia wyłącznie autor — także AI i MCP działające jako ta osoba; owner / admin /
 * manager cudzych notatek nie widzą (warunek w usługach + polityka RLS `user_private`). Osoba z rolą `client`
 * (portal) nie ma dostępu (usługi bez flagi portalu).
 *
 * Usługi (moduł `notes`, `packages/core/src/modules/notes`, router tRPC `quickNotes.*`): `listQuickNotes`,
 * `addQuickNote`, `updateQuickNote`, `completeQuickNote` (odhacz: znika z Dziś), `reopenQuickNote` („Cofnij”),
 * `archiveQuickNote` (cofnięcie działania Copilota — archiwum zamiast usuwania) i `quickNoteToTask` („Zamień
 * w zadanie”: zadanie przez `work.createTask`, notatka odhaczona z odnośnikiem do zadania). MCP: `list_quick_notes`,
 * `add_quick_note`, `update_quick_note`, `complete_quick_note`; Copilot: „zanotuj …” / „zapisz na karteczce …”.
 */

/** Papierowe barwy karteczek (żółta domyślna). Kolory w `apps/web/src/styles/tokens.css` (`--note-*`). */
export const QUICK_NOTE_COLORS = ['yellow', 'pink', 'blue', 'green'] as const;
export type QuickNoteColor = (typeof QUICK_NOTE_COLORS)[number];
export const QUICK_NOTE_DEFAULT_COLOR: QuickNoteColor = 'yellow';

export const QUICK_NOTE_COLOR_LABELS: Record<QuickNoteColor, string> = {
  yellow: 'Żółta',
  pink: 'Różowa',
  blue: 'Błękitna',
  green: 'Zielona',
};

/** Skąd jest notatka: aplikacja, Copilot albo konektor MCP (z kanału aktora). */
export const QUICK_NOTE_SOURCES = ['manual', 'copilot', 'mcp'] as const;
export type QuickNoteSource = (typeof QUICK_NOTE_SOURCES)[number];

export function quickNoteSourceOf(via: Via): QuickNoteSource {
  if (via === 'ai') return 'copilot';
  if (via === 'mcp') return 'mcp';
  return 'manual';
}

export const QUICK_NOTE_MAX_CHARS = 1000;
/** Najwięcej otwartych notatek na liście (Dziś pokazuje wszystkie otwarte; spokojny limit techniczny). */
export const QUICK_NOTE_LIST_MAX = 100;

export const quickNoteBodySchema = z
  .string()
  .trim()
  .min(1, { message: 'Wpisz treść notatki' })
  .max(QUICK_NOTE_MAX_CHARS, { message: `Najwyżej ${QUICK_NOTE_MAX_CHARS} znaków` });

export const quickNoteColorSchema = z.enum(QUICK_NOTE_COLORS);

/** Stan na liście: otwarte (Dziś), odhaczone albo wszystkie (bez archiwum). */
export const QUICK_NOTE_STATES = ['open', 'done', 'all'] as const;
export type QuickNoteState = (typeof QUICK_NOTE_STATES)[number];

export const listQuickNotesInput = z
  .object({
    state: z.enum(QUICK_NOTE_STATES).default('open'),
    limit: z.number().int().min(1).max(QUICK_NOTE_LIST_MAX).default(50),
  })
  .strict();
export type ListQuickNotesInput = z.input<typeof listQuickNotesInput>;

export const addQuickNoteInput = z
  .object({
    body: quickNoteBodySchema,
    color: quickNoteColorSchema.optional(),
    /** „na kiedy” (opcjonalnie; po terminie karteczka ma lekki akcent) */
    dueDate: localDateSchema.nullable().optional(),
  })
  .strict();
export type AddQuickNoteInput = z.input<typeof addQuickNoteInput>;

export const updateQuickNoteInput = z
  .object({
    id: idSchema,
    /** wersja z chwili wczytania (inna = CONFLICT); bez niej — zapis ostatniej zmiany */
    version: versionSchema.optional(),
    body: quickNoteBodySchema.optional(),
    color: quickNoteColorSchema.optional(),
    dueDate: localDateSchema.nullable().optional(),
  })
  .strict()
  .refine((v) => v.body !== undefined || v.color !== undefined || v.dueDate !== undefined, {
    message: 'Brak zmian do zapisania',
  });
export type UpdateQuickNoteInput = z.input<typeof updateQuickNoteInput>;

export const quickNoteIdInput = z.object({ id: idSchema }).strict();

export const quickNoteToTaskInput = z
  .object({
    id: idSchema,
    /** tytuł zadania (domyślnie pierwsza linia notatki) */
    title: taskTitleSchema.optional(),
    clientId: idSchema.nullable().optional(),
    projectId: idSchema.nullable().optional(),
  })
  .strict();
export type QuickNoteToTaskInput = z.input<typeof quickNoteToTaskInput>;

export interface QuickNoteDto {
  id: string;
  body: string;
  color: QuickNoteColor;
  dueDate: string | null;
  /** odhaczona (null = otwarta) */
  doneAt: Date | null;
  source: QuickNoteSource;
  /** zadanie z „Zamień w zadanie” */
  taskId: string | null;
  createdAt: Date;
  updatedAt: Date;
  version: number;
}

export interface QuickNoteToTaskResult {
  note: QuickNoteDto;
  task: { id: string; number: number; title: string };
}

/**
 * Kolejność karteczek jak `notes.list` (serwer): najpierw z terminem (najbliższy), potem bez — w obu grupach
 * najnowsze najpierw. Lista w przeglądarce po dodaniu i zmianie układa się tak samo jak po przeładowaniu.
 */
export function compareQuickNotes(
  a: Pick<QuickNoteDto, 'dueDate' | 'createdAt'>,
  b: Pick<QuickNoteDto, 'dueDate' | 'createdAt'>,
): number {
  if (a.dueDate && !b.dueDate) return -1;
  if (!a.dueDate && b.dueDate) return 1;
  if (a.dueDate && b.dueDate && a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

/** Tytuł zadania z notatki: pierwsza niepusta linia, najwyżej 300 znaków (z wielokropkiem). */
export function quickNoteTaskTitle(body: string): string {
  const first =
    body
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find(Boolean) ?? body.trim();
  return first.length > 300 ? `${first.slice(0, 299).trimEnd()}…` : first;
}

/** Po terminie: termin wcześniej niż dziś (Europe/Warsaw). */
export function isQuickNoteOverdue(note: Pick<QuickNoteDto, 'dueDate' | 'doneAt'>, today: string): boolean {
  return note.doneAt === null && note.dueDate !== null && note.dueDate < today;
}

export const QUICK_NOTE_MESSAGES = {
  notFound: 'Nie znaleziono notatki',
  /** token z zakresem klientów / projektów (bez rekordów wewnętrznych osoby) */
  scopedToken: 'Token z ograniczonym zakresem nie ma dostępu do szybkich notatek',
  noPerson: 'Szybkie notatki ma tylko osoba (nie konto systemowe)',
  emptyHint: 'Zapisz tu to, czego nie chcesz zapomnieć — to nie musi być zadanie.',
  completed: 'Odhaczono notatkę',
  reopened: 'Notatka wróciła na Dziś',
  converted: 'Notatka jest teraz zadaniem',
} as const;
